import { Helmet } from 'react-helmet-async';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, Calendar, Clock, MapPin, Wallet, FileCheck, Plane, Luggage,
  ClipboardCheck, Sparkles, ArrowRight, Brain, Timer, Lightbulb, BarChart3,
  Globe, Zap
} from 'lucide-react';
import { Logo } from '@/components/ui/Logo';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { NewsletterSignup } from '@/components/landing/NewsletterSignup';
import { useAuth } from '@/hooks/useAuth';

const ARTICLE_URL = 'https://odyvoy.com/blog/ai-vs-manual-trip-planning';
const PUBLISH_DATE = '2026-06-24';
const READ_TIME = '10 min read';

const TOC_ITEMS = [
  { id: 'introduction', label: 'Introduction', icon: Lightbulb },
  { id: 'time', label: 'Speed & Efficiency', icon: Timer },
  { id: 'cost', label: 'Cost Accuracy', icon: Wallet },
  { id: 'packing', label: 'Packing Intelligence', icon: Luggage },
  { id: 'visa', label: 'Visa & Compliance', icon: FileCheck },
  { id: 'itinerary', label: 'Itinerary Quality', icon: MapPin },
  { id: 'personalization', label: 'Personalization', icon: Brain },
  { id: 'verdict', label: 'The Verdict', icon: BarChart3 },
];

const comparisonData = [
  {
    factor: 'Research Time',
    manual: '8–15 hours across forums, blogs, and spreadsheets',
    ai: 'Under 5 minutes with destination-aware prompts',
    winner: 'ai',
  },
  {
    factor: 'Budget Accuracy',
    manual: 'Static estimates; easy to under-budget',
    ai: 'Real-time cost indexing with seasonal adjustments',
    winner: 'ai',
  },
  {
    factor: 'Packing Lists',
    manual: 'Generic checklists from blogs; forget climate specifics',
    ai: 'Weather-aware, luggage-limit-optimized packing',
    winner: 'ai',
  },
  {
    factor: 'Visa Research',
    manual: 'Embassy websites, conflicting advice',
    ai: 'Curated, verified requirements with direct application links',
    winner: 'ai',
  },
  {
    factor: 'Personal Touch',
    manual: 'Full creative control over every detail',
    ai: 'Adjustable preferences; still requires human curation',
    winner: 'manual',
  },
  {
    factor: 'Collaboration',
    manual: 'Shared spreadsheets with version chaos',
    ai: 'Synced plans, group budget splitting, real-time updates',
    winner: 'ai',
  },
];

const AiVsManualTripPlanning = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const title = 'AI vs. Manual Trip Planning: A Complete Comparison Guide';
  const description =
    'Is an AI travel planner better than planning a trip manually? We compare speed, cost accuracy, packing, visas, and itinerary quality — and show why OdyVoy wins on efficiency.';

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{`${title} | OdyVoy`}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={ARTICLE_URL} />
        <meta name="keywords" content="ai travel planner, trip planning, plan a trip with ai, manual trip planning, ai vs manual travel planning, smart travel planner, odyvoy" />

        <meta property="og:type" content="article" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={ARTICLE_URL} />
        <meta property="og:image" content="https://odyvoy.com/lovable-uploads/ai-vs-manual-trip-planning-hero.png" />
        <meta property="article:published_time" content={PUBLISH_DATE} />
        <meta property="article:author" content="OdyVoy Travel Team" />
        <meta property="article:section" content="Travel Technology" />
        <meta property="article:tag" content="ai travel planner" />
        <meta property="article:tag" content="trip planning" />
        <meta property="article:tag" content="travel technology" />
        <meta property="article:tag" content="smart packing" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content="https://odyvoy.com/lovable-uploads/ai-vs-manual-trip-planning-hero.png" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": title,
            "description": description,
            "image": "https://odyvoy.com/lovable-uploads/ai-vs-manual-trip-planning-hero.png",
            "datePublished": PUBLISH_DATE,
            "dateModified": PUBLISH_DATE,
            "author": {
              "@type": "Organization",
              "name": "OdyVoy Travel Team"
            },
            "publisher": {
              "@type": "Organization",
              "name": "OdyVoy",
              "logo": {
                "@type": "ImageObject",
                "url": "https://odyvoy.com/lovable-uploads/odyvoy-logo.png"
              }
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": ARTICLE_URL
            },
            "keywords": "ai travel planner, trip planning, manual trip planning, travel technology, smart packing, visa check",
            "articleSection": "Travel Technology",
            "wordCount": 1900
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://odyvoy.com" },
              { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://odyvoy.com/blog" },
              { "@type": "ListItem", "position": 3, "name": title, "item": ARTICLE_URL }
            ]
          })}
        </script>
      </Helmet>

      {/* Header */}
      <header className="sticky top-0 z-40 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-14 sm:h-16 items-center justify-between gap-2 px-3 sm:px-4">
          <button onClick={() => navigate(user ? '/dashboard' : '/')} className="shrink-0" aria-label="Go to homepage">
            <Logo className="h-10 w-10 sm:h-12 sm:w-12 md:h-16 md:w-16" />
          </button>
          <div className="flex items-center gap-1 sm:gap-3">
            <ThemeToggle />
            <Button variant="ghost" size="sm" onClick={() => navigate('/blog')} className="h-9 px-2 sm:px-3" aria-label="Back to blog">
              <ArrowLeft className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Back to Blog</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="container py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Main Content */}
          <article className="lg:col-span-8">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6" aria-label="Breadcrumb">
              <Link to={user ? '/dashboard' : '/'} className="hover:text-foreground">Home</Link>
              <span>/</span>
              <Link to="/blog" className="hover:text-foreground">Blog</Link>
              <span>/</span>
              <span className="text-foreground">Travel Technology</span>
            </nav>

            <Badge variant="secondary" className="mb-4">Travel Technology</Badge>

            <h1 className="text-4xl font-bold mb-4 leading-tight">
              {title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 mb-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4" />
                <span>OdyVoy Travel Team</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <time dateTime={PUBLISH_DATE}>
                  {new Date(PUBLISH_DATE).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </time>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>{READ_TIME}</span>
              </div>
            </div>

            {/* Hero Image */}
            <img
              src="/lovable-uploads/ai-vs-manual-trip-planning-hero.png"
              alt="Split-screen illustration comparing stressed manual trip planning with paper maps versus relaxed AI-powered planning on a smartphone"
              className="w-full h-[300px] sm:h-[400px] object-cover rounded-lg mb-8"
              width={1200}
              height={630}
              {...{ fetchpriority: "high" }}
            />

            {/* Intro */}
            <div className="prose prose-lg dark:prose-invert max-w-none mb-10">
              <p className="text-xl leading-relaxed text-muted-foreground">
                Trip planning has changed. What used to take a full weekend of spreadsheet wrangling, forum digging, and guidebook flipping can now happen in minutes. But is an <strong>AI travel planner</strong> actually better than doing it yourself? In this guide, we compare <strong>AI vs. manual trip planning</strong> across the dimensions that matter most — speed, cost accuracy, packing, visas, and itinerary quality — and show you where OdyVoy fits in.
              </p>
            </div>

            {/* Table of Contents */}
            <Card className="mb-10 border border-border/40 bg-card/60 backdrop-blur-sm">
              <CardHeader>
                <h2 className="text-lg font-semibold leading-none tracking-tight flex items-center gap-2">
                  <ClipboardCheck className="h-5 w-5 text-primary" />
                  What You Will Learn
                </h2>
              </CardHeader>
              <CardContent>
                <ol className="grid sm:grid-cols-2 gap-3">
                  {TOC_ITEMS.map((item, index) => (
                    <li key={item.id}>
                      <button
                        onClick={() => scrollToSection(item.id)}
                        className="flex items-center gap-2 text-sm text-left w-full hover:text-primary transition-colors group"
                      >
                        <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-semibold shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                          {index + 1}
                        </span>
                        <span className="group-hover:underline underline-offset-2">{item.label}</span>
                      </button>
                    </li>
                  ))}
                </ol>
              </CardContent>
            </Card>

            {/* Comparison Table */}
            <section id="introduction" className="mb-12 scroll-mt-24">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 text-primary text-lg font-bold">1</span>
                AI vs. Manual Trip Planning at a Glance
              </h2>
              <div className="prose prose-lg dark:prose-invert max-w-none">
                <p>Before diving into the details, here is how the two approaches stack up on the most common planning tasks:</p>
              </div>
              <div className="overflow-x-auto mt-4">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-2 font-semibold">Factor</th>
                      <th className="text-left py-3 px-2 font-semibold text-muted-foreground">Manual Planning</th>
                      <th className="text-left py-3 px-2 font-semibold text-primary">AI Planning (OdyVoy)</th>
                      <th className="text-left py-3 px-2 font-semibold">Winner</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonData.map((row) => (
                      <tr key={row.factor} className="border-b border-border/40">
                        <td className="py-3 px-2 font-medium">{row.factor}</td>
                        <td className="py-3 px-2 text-muted-foreground">{row.manual}</td>
                        <td className="py-3 px-2 text-primary">{row.ai}</td>
                        <td className="py-3 px-2">
                          <Badge variant={row.winner === 'ai' ? 'default' : 'secondary'}>
                            {row.winner === 'ai' ? 'AI' : 'Manual'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Speed & Efficiency */}
            <section id="time" className="mb-12 scroll-mt-24">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 text-primary text-lg font-bold">2</span>
                Speed & Efficiency: Hours vs. Minutes
              </h2>
              <div className="prose prose-lg dark:prose-invert max-w-none">
                <p>Manual <strong>trip planning</strong> is a research marathon. You are juggling browser tabs for flights, cross-referencing hotel reviews, checking visa rules on embassy sites, and building spreadsheets for budgets and packing lists. For a two-week international trip, this can easily consume 8 to 15 hours.</p>
                <p>An <strong>AI travel planner</strong> like OdyVoy collapses that timeline. By understanding your destination, dates, and travel style, it can generate a structured itinerary, a weather-aware packing list, and a realistic budget in under five minutes. The time savings compound when you factor in revisions: tweaking a spreadsheet takes another hour; tweaking an AI plan takes a prompt.</p>
                <p><strong>Bottom line:</strong> If you enjoy the research process, manual planning has its charms. If you want to spend your time experiencing the trip rather than planning it, AI is the clear winner.</p>
              </div>
            </section>

            {/* Cost Accuracy */}
            <section id="cost" className="mb-12 scroll-mt-24">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 text-primary text-lg font-bold">3</span>
                Cost Accuracy: Guessing vs. Data-Driven Budgets
              </h2>
              <div className="prose prose-lg dark:prose-invert max-w-none">
                <p>One of the most common manual planning pitfalls is underestimating costs. It is easy to overlook airport transfers, resort fees, visa costs, or the fact that dinner in Tokyo costs twice what it does in Bangkok. Spreadsheets are static; reality is dynamic.</p>
                <p>OdyVoy’s budget tools are built on real data. The app factors in seasonal price swings, local currency fluctuations, and destination-specific costs (like Japan’s rail passes or Europe’s city taxes). It also splits group expenses automatically, so no one is left holding the bill at the end of the trip.</p>
                <p>That said, manual planners who are meticulous can achieve similar accuracy — it just takes significantly more effort and constant cross-referencing.</p>
              </div>
            </section>

            {/* Packing Intelligence */}
            <section id="packing" className="mb-12 scroll-mt-24">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 text-primary text-lg font-bold">4</span>
                Packing Intelligence: Generic Lists vs. Smart Lists
              </h2>
              <div className="prose prose-lg dark:prose-invert max-w-none">
                <p>Generic packing checklists are everywhere — and they are rarely right. They do not know if you are going to rainy Scotland in March or dry Morocco in July. They do not know your airline’s baggage weight limit. And they definitely do not know that your hotel has a strict no-liquid policy.</p>
                <p>OdyVoy’s packing assistant solves this by connecting your destination, dates, and luggage constraints to a database of weather patterns, local customs, and airline rules. It auto-optimizes: if your carry-on is only 7 kg, it prioritizes multi-use clothing and travel-sized essentials. If you are visiting temples in Thailand, it reminds you to pack modest cover-ups. If rain is forecast, it shifts sandals lower and waterproof layers higher.</p>
                <p>Manual planners can achieve this too, but only after reading dozens of packing forums and weather forecasts. AI just asks three questions and does the rest.</p>
              </div>
            </section>

            {/* Visa & Compliance */}
            <section id="visa" className="mb-12 scroll-mt-24">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 text-primary text-lg font-bold">5</span>
                Visa & Compliance: Confusion vs. Clarity
              </h2>
              <div className="prose prose-lg dark:prose-invert max-w-none">
                <p>Visa rules are a moving target. A blog post from 2024 saying Vietnam offers visa-free entry may be outdated by 2026. Manual planners have to verify every rule across multiple embassy websites, often in foreign languages, and still risk missing a subtle requirement like six months passport validity or a yellow fever certificate.</p>
                <p>OdyVoy uses curated visa data for over 180 destinations, updated to reflect current policies. Instead of vague "check before you travel" advice, you get exact requirements, processing times, and direct links to official application portals. For destinations with e-visas or ESTA-style programs, OdyVoy labels them explicitly so there is no guesswork.</p>
                <p>For example, if you are planning a trip to Indonesia, OdyVoy routes you straight to the official e-visa portal rather than a third-party blog. That alone can save hours and prevent costly mistakes.</p>
              </div>
            </section>

            {/* Itinerary Quality */}
            <section id="itinerary" className="mb-12 scroll-mt-24">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 text-primary text-lg font-bold">6</span>
                Itinerary Quality: Over-Scheduling vs. Balance
              </h2>
              <div className="prose prose-lg dark:prose-invert max-w-none">
                <p>Manual planners often fall into two traps: copying a generic "top 10 things to do" list and cramming too much into each day, or under-planning and missing must-book experiences like the Uffizi Gallery or the Inca Trail.</p>
                <p>An AI travel planner can strike a better balance by understanding travel times between neighborhoods, opening hours, and the realistic pace of a given city. OdyVoy’s itineraries group activities by location, leave buffer time for spontaneity, and flag attractions that require advance booking. For multi-city trips, the multi-city planner (available for First Class subscribers) even accounts for packing resets and visa re-checks at each border.</p>
                <p>The best itineraries are still a collaboration: AI proposes the structure, and you tweak it to match your interests. That hybrid approach beats either pure manual or pure AI planning alone.</p>
              </div>
            </section>

            {/* Personalization */}
            <section id="personalization" className="mb-12 scroll-mt-24">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 text-primary text-lg font-bold">7</span>
                Personalization: One-Size-Fits-All vs. Truly Yours
              </h2>
              <div className="prose prose-lg dark:prose-invert max-w-none">
                <p>This is where manual planning traditionally shines. A spreadsheet built by you reflects your quirks: your obsession with coffee shops, your aversion to crowds, your need for afternoon naps. No generic algorithm knows you that well.</p>
                <p>But AI is catching up fast. OdyVoy lets you set traveler profiles (solo, couple, family, digital nomad), activity preferences (adventure, culture, food, relaxation), and even health considerations. The more you use it, the more it learns. Over time, an AI plan can feel just as personal as a handmade one — and it updates instantly when your flight gets delayed or the weather shifts.</p>
                <p>For now, the edge goes to manual planning for deeply niche trips (e.g., a photography-focused trek through Patagonia). For everything else, AI personalization is more than good enough.</p>
              </div>
            </section>

            {/* The Verdict */}
            <section id="verdict" className="mb-12 scroll-mt-24">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 text-primary text-lg font-bold">8</span>
                The Verdict: When to Use AI, When to Go Manual
              </h2>
              <div className="prose prose-lg dark:prose-invert max-w-none">
                <p>Neither approach is perfect for every traveler. Here is a simple framework to decide:</p>
                <h3>Choose AI Trip Planning When:</h3>
                <ul>
                  <li>You are short on time and want a solid plan in minutes</li>
                  <li>You are visiting a destination for the first time and need foundational research</li>
                  <li>You want real-time budget tracking and group cost splitting</li>
                  <li>You need weather-aware packing lists and up-to-date visa info</li>
                  <li>You are planning a multi-city trip with complex logistics</li>
                </ul>
                <h3>Choose Manual Planning When:</h3>
                <ul>
                  <li>You enjoy the research process as part of the trip excitement</li>
                  <li>Your trip is highly specialized (photography, culinary, extreme sports)</li>
                  <li>You have deep local knowledge and want total creative control</li>
                  <li>You are traveling to a region with limited digital infrastructure</li>
                </ul>
                <p>For most modern travelers, the answer is not either/or — it is <strong>both</strong>. Use an <strong>AI travel planner</strong> like OdyVoy to build the framework, then layer in your personal touches. You get the speed and accuracy of AI plus the soul of a trip that feels truly yours.</p>
                <p>Ready to plan your next trip the smart way? <Link to="/blog/how-to-plan-a-trip" className="text-primary hover:underline">Read our complete step-by-step trip planning guide</Link> and see how OdyVoy fits into every stage of the journey.</p>
              </div>
            </section>

            {/* CTA Section */}
            <Card className="mb-10 border border-primary/20 bg-primary/5">
              <CardContent className="p-6 sm:p-8">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <div className="p-3 rounded-xl bg-primary/10 shrink-0">
                    <Zap className="h-6 w-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold mb-1">Try OdyVoy for Your Next Trip</h3>
                    <p className="text-sm text-muted-foreground">
                      Plan faster, pack smarter, and travel with confidence. Start with a free trip plan and upgrade when you are ready.
                    </p>
                  </div>
                  <Button onClick={() => navigate(user ? '/dashboard' : '/auth')} className="shrink-0">
                    {user ? 'Go to Dashboard' : 'Get Started Free'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Newsletter */}
            <div className="mb-10">
              <NewsletterSignup />
            </div>
          </article>

          {/* Sidebar */}
          <aside className="lg:col-span-4">
            <div className="sticky top-24 space-y-6">
              {/* Table of Contents Sidebar */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Table of Contents</CardTitle>
                </CardHeader>
                <CardContent>
                  <ol className="space-y-2">
                    {TOC_ITEMS.map((item, index) => (
                      <li key={item.id}>
                        <button
                          onClick={() => scrollToSection(item.id)}
                          className="flex items-center gap-2 text-sm text-left w-full hover:text-primary transition-colors group"
                        >
                          <span className="flex items-center justify-center w-5 h-5 rounded-full bg-muted text-muted-foreground text-xs font-semibold shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                            {index + 1}
                          </span>
                          <span className="group-hover:underline underline-offset-2">{item.label}</span>
                        </button>
                      </li>
                    ))}
                  </ol>
                </CardContent>
              </Card>

              {/* Quick Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <BarChart3 className="h-4 w-4 text-primary" />
                    Quick Stats
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Time Saved</span>
                    <span className="font-semibold">~12 hrs</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Destinations Covered</span>
                    <span className="font-semibold">180+</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Packing Accuracy</span>
                    <span className="font-semibold">Weather-aware</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Budget Tracking</span>
                    <span className="font-semibold">Real-time</span>
                  </div>
                </CardContent>
              </Card>

              {/* Related Guides */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Related Guides</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Link to="/blog/how-to-plan-a-trip" className="flex items-start gap-3 group">
                    <div className="p-2 rounded-lg bg-primary/10 shrink-0">
                      <Globe className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-medium group-hover:text-primary transition-colors">How to Plan a Trip from Scratch</p>
                      <p className="text-xs text-muted-foreground">Step-by-step checklist</p>
                    </div>
                  </Link>
                  <Link to="/blog/how-to-pack-light-without-leaving-the-essentials-behind" className="flex items-start gap-3 group">
                    <div className="p-2 rounded-lg bg-primary/10 shrink-0">
                      <Luggage className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-medium group-hover:text-primary transition-colors">How to Pack Light</p>
                      <p className="text-xs text-muted-foreground">Essential packing strategies</p>
                    </div>
                  </Link>
                  <Link to="/blog/how-to-find-the-best-flight-deals-in-2025-2026" className="flex items-start gap-3 group">
                    <div className="p-2 rounded-lg bg-primary/10 shrink-0">
                      <Plane className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-medium group-hover:text-primary transition-colors">Best Flight Deals</p>
                      <p className="text-xs text-muted-foreground">Save on airfare</p>
                    </div>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
};

export default AiVsManualTripPlanning;
